import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { getShelfShoppingPackages, getUserDetailsById, sendNotificationFunction } from "../../../helper/functions";
import { setSelectedPackage } from "../../../store/commonSlice";

export const packageHistory = async (
  oldValue,
  newValue,
  agentName,
  packageCollectionName
) => {
  const docRef = doc(db, packageCollectionName, oldValue?.id);
  const changes = [];

  // Array of fields to compare
  const fieldsToCompare = [
    {
      key: "brand.brandName",
      getOld: (o) => (o?.brand?.brandName || ""),
      getNew: (n) => (n?.brand?.brandName || ""),
      getSentence: (o, n, id) =>
        `<strong>${id}</strong> Package Merchant of <strong>${o?.brand?.brandName}</strong> was changed to <strong>${n?.brand?.brandName}</strong>`
    },
    {
      key: "merchantIndicator",
      getOld: (o) => (o?.merchantIndicator || ""),
      getNew: (n) => (n?.merchantIndicator || ""),
      getSentence: (o, n, id) =>
        `<strong>${id}</strong> Package Merchant Indicator of <strong>${o?.merchantIndicator}</strong> was changed to <strong>${n?.merchantIndicator}</strong>`
    },
    {
      key: "packageTotalWeight",
      getOld: (o) => (o?.packageTotalWeight || 0),
      getNew: (n) => (n?.packageTotalWeight || 0),
      getSentence: (o, n, id) =>
        `<strong>${id}</strong> Package Weight of <strong>${o?.packageTotalWeight}</strong> was changed to <strong>${n?.packageTotalWeight}</strong>`
    },
    {
      key: "shelfId",
      getOld: (o) => (o?.shelfId || ""),
      getNew: (n) => (n?.shelfId || ""),
      getSentence: (o, n, id) =>
        `<strong>${id}</strong> Package Shelf ID of <strong>${o?.shelfId}</strong> was changed to <strong>${n?.shelfId}</strong>`
    },
    // Add more fields as needed
  ];

  // Loop over fields and compare
  fieldsToCompare.forEach(field => {
    if (field.getOld(oldValue) !== field.getNew(newValue)) {
      changes.push({
        sentence: field.getSentence(oldValue, newValue, oldValue?.packageTrackingId),
        updatedBy: ` by <strong>${agentName}</strong>`,
        date: new Date(),
      });
    }
  });

  // Special case for dimensions
  if (
    oldValue.dimensionH &&
    oldValue.dimensionL &&
    oldValue.dimensionW &&
    (oldValue.dimensionH !== newValue.dimensionH ||
      oldValue.dimensionL !== newValue.dimensionL ||
      oldValue.dimensionW !== newValue.dimensionW)
  ) {
    changes.push({
      sentence: `Dimension of <strong>${oldValue?.packageTrackingId}</strong> package <strong>${oldValue.dimensionL}</strong>(Length) <strong>${oldValue.dimensionW}</strong>(Width) <strong>${oldValue.dimensionH}</strong>(Height) was changed to <strong>${newValue.dimensionL}</strong>(Length) <strong>${newValue.dimensionW}</strong>(Width) <strong>${newValue.dimensionH}</strong>(Height)`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
    });
  }

  if (changes.length === 0) return;

  // Update Firestore
  try {
    const docSnapshot = await getDoc(docRef);
    if (!docSnapshot.exists()) return;

    const data = docSnapshot.data();
    if (!data.history) {
      data.history = {
        preShopping: null,
        verification: null,
        postShopping: null,
        preShipping: null,
        postShipping: null,
        finalPrice: null,
        package: [],
      };
    }
    if (!Array.isArray(data.history.package)) {
      data.history.package = [];
    }
    data.history.package = [...data.history.package, ...changes];

    await updateDoc(docRef, { history: data.history });
  } catch (error) {
    console.error("Error updating package history:", error);
  }
};

export const preShoppingHistory = async (
  oldValue,
  newValue,
  agentName,
  packageActiveData,
  packageCollectionName
) => {
  const docRef = doc(db, packageCollectionName, packageActiveData?.id);
  const changes = [];

  // Array of fields to compare
  const fieldsToCompare = [
    {
      key: "customizeRequestCost",
      getOld: (o) => (o.customizeRequestCost || 0),
      getNew: (n) => n.preShopping.customizeRequestCost,
      getSentence: (o, n, id) =>
        `The status for  <strong>${id}</strong> was updated with Customize Request cost of <strong>${n.preShopping.customizeRequestCost} by ${agentName}</strong> `,
      getNotificationMessage: (o, n, id) =>
        `Customize Request cost for item ${o.shortenedName || id} updated to $${n.preShopping.customizeRequestCost}.`,
    },
    {
      key: "price",
      getOld: (o) => o.price,
      getNew: (n) => n.preShopping.price,
      getSentence: (o, n, id) =>
        `The price per item <strong>${o.shortenedName}</strong> was updated from <strong>$${o.price}</strong> to <strong>$${n.preShopping.price}</strong>`,
      getNotificationMessage: (o, n, id) =>
        `The price for ${o.shortenedName || id} changed from $${o.price} to $${n.preShopping.price}.`,
    },
    {
      key: "quantity",
      getOld: (o) => o.quantity,
      getNew: (n) => n.preShopping.quantity,
      getSentence: (o, n, id) =>
        `The Quantity <strong>${o.shortenedName}</strong> was updated from <strong>${o.quantity}</strong> to <strong>${n.preShopping.quantity}</strong>`,
      getNotificationMessage: (o, n, id) =>
        `The quantity for ${o.shortenedName || id} changed from ${o.quantity} to ${n.preShopping.quantity}.`,
    },
    {
      key: "tax",
      getOld: (o) => o.tax,
      getNew: (n) => n.preShopping.tax,
      getSentence: (o, n, id) =>
        `Tax in the amount of <strong>$${n.preShopping.tax}</strong> was updated for <strong>${o.shortenedName}</strong>.`,
      getNotificationMessage: (o, n, id) =>
        `The tax for ${o.shortenedName || id} changed from $${o.tax} to $${n.preShopping.tax}.`,
    },
    {
      key: "weight",
      getOld: (o) => o.weight,
      getNew: (n) => n.preShopping.weight,
      getSentence: (o, n, id) =>
        `The Weight per item <strong>${o.shortenedName}</strong> was updated from <strong>${o.weight}lb</strong> to <strong>${n.preShopping.weight}lb</strong>`,
      getNotificationMessage: (o, n, id) =>
        `The weight for ${o.shortenedName || id} changed from ${o.weight}lb to ${n.preShopping.weight}lb.`,
    },
    {
      key: "promoValue",
      getOld: (o) => o.promoValue || 0,
      getNew: (n) => n.preShopping.promoValue,
      getSentence: (o, n, id) =>
        `The status for ${id} was updated with Promo Value of <strong>${n.preShopping.promoValue}<strong>. You will get a credit towards your purchase.  by <strong>${agentName}</strong>.`,
      getNotificationMessage: (o, n, id) =>
        `Promo value for ${o.shortenedName || id} updated to $${n.preShopping.promoValue}.`,
    },
    {
      key: "merchantShippingCost",
      getOld: (o) => o.merchantShippingCost,
      getNew: (n) => n.preShopping.merchantShippingCost,
      getSentence: (o, n, id) =>
        `The status for ${id} was updated with Merchant Shipping cost of <strong>${n.preShopping.merchantShippingCost}<strong>  by {Admin name}.`,
      getNotificationMessage: (o, n, id) =>
        `Merchant shipping cost for ${o.shortenedName || id} updated to $${n.preShopping.merchantShippingCost}.`,
    },
  ];

  fieldsToCompare.forEach(field => {
    if (field.getOld(oldValue) && field.getNew(newValue) && field.getOld(oldValue) !== field.getNew(newValue)) {
      changes.push({
        key: field.key,
        previousValue: field.getOld(oldValue),
        newValue: field.getNew(newValue),
        sentence: field.getSentence(oldValue, newValue, packageActiveData?.id),
        notificationMessage: field.getNotificationMessage
          ? field.getNotificationMessage(oldValue, newValue, packageActiveData?.id)
          : null,
        updatedBy: ` by <strong>${agentName}</strong>`,
        date: new Date(),
        isSeen: false,
      });
    }
  });

  // Special status logic
  if (oldValue.preShoppingStatus !== newValue.preShopping.preShoppingStatus) {
    if (newValue.preShopping.preShoppingStatus === "Cancel Item") {
      changes.push({
        key: "preShoppingStatus",
        previousValue: oldValue.preShoppingStatus,
        newValue: newValue.preShopping.preShoppingStatus,
        sentence: `<strong>${oldValue.shortenedName}</strong> was canceled`,
        notificationMessage: `${oldValue.shortenedName} was canceled.`,
        updatedBy: ` by <strong>${agentName}</strong>`,
        date: new Date(),
        isSeen: false,
      });
    } else if (newValue.preShopping.preShoppingStatus === "Out of Stock") {
      changes.push({
        key: "preShoppingStatus",
        previousValue: oldValue.preShoppingStatus,
        newValue: newValue.preShopping.preShoppingStatus,
        sentence: `<strong>${oldValue.shortenedName}</strong> is Out of Stock`,
        notificationMessage: `${oldValue.shortenedName} is Out of Stock.`,
        updatedBy: ` by <strong>${agentName}</strong>`,
        date: new Date(),
        isSeen: false,
      });
    } else if (newValue.preShopping.preShoppingStatus === "Exporting Contraband") {
      changes.push({
        key: "preShoppingStatus",
        previousValue: oldValue.preShoppingStatus,
        newValue: newValue.preShopping.preShoppingStatus,
        sentence: `<strong>${oldValue.shortenedName}</strong> was marked as an Export contraband`,
        notificationMessage: `${oldValue.shortenedName} was marked as Exporting Contraband.`,
        updatedBy: ` by <strong>${agentName}</strong>`,
        date: new Date(),
        isSeen: false,
      });
    } else if (newValue.preShopping.preShoppingStatus === "Importing Contraband") {
      changes.push({
        key: "preShoppingStatus",
        previousValue: oldValue.preShoppingStatus,
        newValue: newValue.preShopping.preShoppingStatus,
        sentence: `<strong>${oldValue.shortenedName}</strong> was marked as an Importing Contraband`,
        notificationMessage: `${oldValue.shortenedName} was marked as Importing Contraband.`,
        updatedBy: ` by <strong>${agentName}</strong>`,
        date: new Date(),
        isSeen: false,
      });
    }
  }

  if (oldValue.promoCode !== newValue.preShopping.promoCode) {
    if (oldValue.promoCode) {
      changes.push({
        key: "promoCode",
        sentence: `Promo code for <strong>${oldValue.shortenedName}</strong> was update with <strong>${newValue.preShopping.promoCode} from <strong>${oldValue.promoCode}</strong>`,
        notificationMessage: `Promo code for ${oldValue.shortenedName} updated from ${oldValue.promoCode} to ${newValue.preShopping.promoCode}.`,
        date: new Date(),
        isSeen: false,
      });
    } else {
      changes.push({
        key: "promoCode",
        sentence: `A new Promo code for <strong>${oldValue.shortenedName}</strong> was entered to help you save on this item.`,
        notificationMessage: `A new promo code for ${oldValue.shortenedName} was entered.`,
        date: new Date(),
        isSeen: false,
      });
    }
  }

  if (changes.length === 0) return [];

  try {
    const docSnapshot = await getDoc(docRef);
    if (!docSnapshot.exists()) return [];
    const data = docSnapshot.data();
    if (!data.history) {
      data.history = {
        preShopping: [],
        verification: null,
        postShopping: null,
        preShipping: null,
        postShipping: null,
        finalPrice: null,
        package: null,
      };
    }
    if (!Array.isArray(data.history.preShopping)) {
      data.history.preShopping = [];
    }
    data.history.preShopping = [...data.history.preShopping, ...changes];
    await updateDoc(docRef, { history: data.history });
  } catch (error) {
    console.error("Error updating preShopping history:", error);
  }
  return changes;
};

export const verificationHistory = (
  reason,
  packageActiveData,
  agentName,
  packageCollectionName
) => {
  const docRef = doc(db, packageCollectionName, packageActiveData?.id);

  const changes = [];
  const diff = {
    sentence: `Verification was requested due to <strong>${reason}</strong>`,
    updatedBy: ` by <strong>${agentName}</strong>`,
    date: new Date(),
    isSeen: false,
  };
  changes.push(diff);

  getDoc(docRef).then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      // console.log(data, "data>>");
      if (!data.history) {
        data.history = {
          preShopping: null,
          verification: null,
          postShopping: null,
          preShipping: null,
          postShipping: null,
          finalPrice: null,
          package: null,
        };
      }

      // Update postShoppingStatus as an array of objects
      data.history.verification = [...data?.history?.verification || [], ...changes];

      // Now, update the Firestore document with the modified history data
      updateDoc(docRef, data)
        .then((res) => console.log())
        .catch((error) => console.error("Error updating document: ", error));
    }
  });
};

export const postShoppingHistory = (
  dispatch,
  oldValue,
  value,
  agentName,
  packageActiveData,
  packageCollectionName
) => {
  // console.log(
  //   oldValue,
  //   value,
  //   agentName,
  //   packageActiveData,
  //   packageCollectionName,
  //   "old new value in post shopping history"
  // );
  const docRef = doc(db, packageCollectionName, packageActiveData?.id);

  const changes = [];

  // selectedItems.map((item, v) => {
  if (value.postShoppingStatus === "Placed order") {
    const diff = {
      sentence: `The Status <strong>${packageActiveData?.id}</strong> was updated to <strong>${value.postShoppingStatus}</strong><br />
        <strong>Order No.:</strong> ${value.orderNumber}`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  } else if (value.postShoppingStatus === "Order Received") {
    const diff = {
      sentence: `The Status <strong>${packageActiveData?.id}</strong> was updated to <strong>${value.postShoppingStatus}</strong><br />
        <strong>Tracing No.:</strong> ${value.tracingNumber}`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  } else if (value.postShoppingStatus === "Return by Admin") {
    const diff = {
      sentence: `Admin returned <strong>${packageActiveData?.id}</strong> to the shopping vendor. Please contact admin for more information.`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  } else if (value.postShoppingStatus === "Customer requested return") {
    const diff = {
      sentence: `Your request to return <strong>${packageActiveData?.id}</strong> was completed. Any eligible refund will be applied.`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  } else {
    const diff = {
      sentence: `The Status <strong>${packageActiveData?.id}</strong> was updated to <strong>${value.postShoppingStatus}</strong>`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  }
  // });
  getDoc(docRef).then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      // console.log(data, "data>>");
      if (!data.history) {
        data.history = {
          preShopping: null,
          verification: null,
          postShopping: null,
          preShipping: null,
          postShipping: null,
          finalPrice: null,
          package: null,
        };
      }

      // Update postShoppingStatus as an array of objects
      data.history.postShopping = [...data?.history?.postShopping || [], ...changes];

      // Now, update the Firestore document with the modified history data
      updateDoc(docRef, data)
        .then(async(res) => {
          const updatedPackageDetails = await getShelfShoppingPackages([{
            key:"__name__",
            operator:"==",
            value:packageActiveData?.id
          }])
          console.log(updatedPackageDetails,"updatedPackageDetails shopping");
          dispatch(setSelectedPackage(updatedPackageDetails[0]));
        })
        .catch((error) => console.error("Error updating document: ", error));
    }
  });
};

export const preShippingHistory = (
  oldValue,
  value,
  agentName,
  packageActiveData,
  packageCollectionName
) => {
  console.log(
    oldValue,
    value,
    agentName,
    packageActiveData,
    packageCollectionName,
    "old new value in pre shipping history"
  );

  const docRef = doc(db, packageCollectionName, packageActiveData?.id);

  const changes = [];

  // selectedItems.map((item, v) => {
  const diff = {
    sentence: `The Status of <strong> ${packageActiveData?.id} </strong> was updated to <strong>${value.preShippingStatus}</strong>`,
    updatedBy: ` by <strong>${agentName}</strong>`,
    date: new Date(),
    isSeen: false,
  };
  changes.push(diff);

  // console.log(changes, ">>>ccchngessss");

  getDoc(docRef).then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      if (!data.history) {
        data.history = {
          preShopping: null,
          verification: null,
          postShopping: null,
          preShipping: null,
          postShipping: null,
          finalPrice: null,
          package: null,
        };
      }

      data.history.preShipping = [...data?.history?.preShipping || [], ...changes];

      updateDoc(docRef, data)
        .then((res) => console.log())
        .catch((error) => console.error("Error updating document: ", error));
    }
  });
  // });
};

export const postShippingHistory = (
  itemData,
  value,
  agentName,
  packageActiveData,
  packageCollectionName
) => {
  const docRef = doc(db, packageCollectionName, packageActiveData?.id);

  const changes = [];

  // selectedItems.map((item, v) => {
  const diff = {
    sentence: `The Item <strong> ${packageActiveData?.id} </strong> was updated to <strong>${value}</strong><br />`,
    updatedBy: ` by <strong>${agentName}</strong>`,
    date: new Date(),
    isSeen: false,
  };
  changes.push(diff);

  getDoc(docRef).then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      if (!data.history) {
        data.history = {
          preShopping: null,
          verification: null,
          postShopping: null,
          preShipping: null,
          postShipping: null,
          finalPrice: null,
          package: null,
        };
      }
      data.history.postShipping = [...data?.history?.postShipping || [], ...changes];

      updateDoc(docRef, data)
        .then((res) => console.log())
        .catch((error) => console.error("Error updating document: ", error));
    }
  });
  // });
};

export const finalPriceHistory = (
  orderactiveData,
  value,
  agentName,
  orderactive,
  Finalbalance
) => {
  // console.log(
  //   orderactiveData,
  //   value,
  //   agentName,
  //   orderactive,
  //   Finalbalance,
  //   "selectedItems,value, agentName,orderactive"
  // );

  const docRef = doc(db, "order", orderactive);

  const changes = [];

  // if (orderactiveData.packagingWeight !== value.packagingWeight) {
  const diff1 = {
    sentence: `Packaging weight of <strong>${value.packagingWeight}lb</strong> was added`,
    updatedBy: ` by <strong>${agentName}</strong>`,
    date: new Date(),
    isSeen: false,
  };
  changes.push(diff1);
  // }
  // if (orderactiveData?.dimensionalWeightPrice.storageFeeTotal !== value.storageFeeTotal) {
  const diff2 = {
    sentence: `Total storage fee for <strong>${value.storageDays} days</strong> in the amout of <strong>$${value.storageFeeTotal}</strong> was added`,
    updatedBy: ` by <strong>${agentName}</strong>`,
    date: new Date(),
    isSeen: false,
  };
  changes.push(diff2);
  // }
  // if (
  //   orderactiveData.dimensionalWeightPrice.height !== value.height ||
  //   orderactiveData.dimensionalWeightPrice.length !== value.length ||
  //   orderactiveData.dimensionalWeightPrice.width !== value.width ||
  //   orderactiveData.dimensionalWeightPrice.price !== value.price
  // ) {
  const diff3 = {
    sentence: `Dimensional weight price (DIM) of <strong>${value.length}</strong>(Length) <strong>${value.width}</strong>(Width) <strong>${value.height}</strong>(Height) was charged in the amount of <strong>$${value.price}</strong> was charged`,
    updatedBy: ` by <strong>${agentName}</strong>`,
    date: new Date(),
    isSeen: false,
  };
  changes.push(diff3);
  // }
  if (orderactiveData.amountDue !== Finalbalance && Finalbalance < 0) {
    const diff = {
      sentence: `A refund in the amount of <strong>$${Finalbalance}</strong> was issued`,
      updatedBy: ` by <strong>${agentName}</strong>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  }
  if (orderactiveData.amountDue !== Finalbalance && Finalbalance > 0) {
    const diff = {
      sentence: `You have a balance in the amount of <strong>$${Finalbalance}</strong> Please <button class="btn btn-primary">PAY NOW</button>`,
      date: new Date(),
      isSeen: false,
    };
    changes.push(diff);
  }

  getDoc(docRef).then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      if (!data.history) {
        data.history = {
          preShopping: null,
          verification: null,
          postShopping: null,
          preShipping: null,
          postShipping: null,
          finalPrice: null,
        };
      }
      data.history.finalPrice = [...data?.history?.finalPrice || [], ...changes];

      updateDoc(docRef, data)
        .then((res) => console.log())
        .catch((error) => console.error("Error updating document: ", error));
    }
  });
};

export const isValidUrl = (urlString) => {
  var urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // validate protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // validate port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const MediaDownloadLink = ({ mediaUrl, fileName }) => {
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = mediaUrl;
    link.download = fileName; // Specify the desired file name
    link.click();
  };

  return <button onClick={handleDownload}>Download Media</button>;
};


export const sendShoppingDetailsNotification = async (change,itemsData,packageActiveData) => {
  const userDetails = await getUserDetailsById(itemsData?.userId)
  if(change.key === "price"){
        await sendNotificationFunction({
          eventType:"SHOPPING",
          eventStatus:"SHOPPING_ITEM_PRICE_UPDATE",
          fcmToken:userDetails?.fcmToken,
          userId:userDetails?.id,
          mailId:userDetails?.emailId,
          payload:{
            shoppingId:packageActiveData?.id,
            itemName:itemsData?.itemName,
            oldPrice:change?.previousValue,
            newPrice:change.newValue||""
          }
        })
  }
  else if(change.key === "quantity"){
    await sendNotificationFunction({
      eventType:"SHOPPING",
      eventStatus:"SHOPPING_ITEM_QTY_UPDATE",
      fcmToken:userDetails?.fcmToken,
      userId:userDetails?.id,
      mailId:userDetails?.emailId,
      payload:{
        shoppingId:packageActiveData?.id,
        itemName:itemsData?.itemName,
        oldQty:change?.previousValue,
        newQty:change.newValue||""
      }
    })
  }
  else if(change.key === "weight"){
    await sendNotificationFunction({
      eventType:"SHOPPING",
      eventStatus:"SHOPPING_ITEM_WEIGHT_UPDATE",
      fcmToken:userDetails?.fcmToken,
      userId:userDetails?.id,
      mailId:userDetails?.emailId,
      payload:{
        shoppingId:packageActiveData?.id,
        itemName:itemsData?.name,
        oldWeight:change?.previousValue,
        newWeight:change.newValue||""
      }
    })
  }
  else if(change.key === "merchantShippingCost"){
    await sendNotificationFunction({
      eventType:"SHOPPING",
      eventStatus:"SHOPPING_ITEM_MERCHANT_SHIPPING_COST_UPDATE",
      fcmToken:userDetails?.fcmToken,
      userId:userDetails?.id,
      mailId:userDetails?.emailId,
      payload:{
        shoppingId:packageActiveData?.id,
        itemName:itemsData?.itemName,
        oldPrice:change?.previousValue,
        newPrice:change.newValue||""
      }
    })
  }
  else if(change.key === "tax"){
    await sendNotificationFunction({
      eventType:"SHOPPING",
      eventStatus:"SHOPPING_ITEM_TAX_PRICE_UPDATE",
      fcmToken:userDetails?.fcmToken,
      userId:userDetails?.id,
      mailId:userDetails?.emailId,
      payload:{
        shoppingId:packageActiveData?.id,
        itemName:itemsData?.itemName,
        oldPrice:change?.previousValue,
        newPrice:change.newValue||""
      }
    })
  }
  else if(change.key === "customizeRequestCost"){
    await sendNotificationFunction({
      eventType:"SHOPPING",
      eventStatus:"SHOPPING_ITEM_CUSTOMIZE_REQUEST_COST_UPDATE",
      fcmToken:userDetails?.fcmToken,
      userId:userDetails?.id,
      mailId:userDetails?.emailId,
      payload:{
        shoppingId:packageActiveData?.id,
        itemName:itemsData?.itemName,
        oldPrice:change?.previousValue,
        newPrice:change.newValue||""
      }
    })
  }

}